import Tooltip from '@mui/material/Tooltip';
import forge from "node-forge";

import { disputesRef, jobsRef } from './firebase';
import { routes } from './App/routes';
import { PUT } from './API/put';
import { getUserInfo } from './storage';

const user_info = getUserInfo();

const formatNumber = (value, decimals = 2) => Number(value).toFixed(decimals);
const parseTimestamp = (timestamp) => new Date(parseInt(timestamp));
const pad2 = (n) => n?.toString().padStart(2, '0');

const runningJobStutes = [1, 2, 5, 6, 7, 12];

export const isRunningJob = (status) => runningJobStutes.includes(status);

export const calHourlyRate = (skillValue, skillCommission, timeWindow) => {
    const sum = skillValue * Math.pow(0.95, timeWindow) * ((100 + skillCommission) / 100);
    return formatNumber(sum);
};

export const diffMinutes = (time) => {
    const today = new Date();
    const startTime = new Date(`${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()} ${time}`);
    const diff = (startTime - today) / 60000;
    return Math.max(Math.abs(Math.round(diff)), 15);
};

export const minutesToHHMM = (value) => {
    const hours = Math.floor(value / 60);
    const minutes = value % 60;
    return `${pad2(hours)}:${pad2(minutes)}`;
};

export const formatHHMM = (timestamp, isShow12Hour = false) => {
    const options = {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/London',
        hour12: isShow12Hour,
    };
    return parseTimestamp(timestamp).toLocaleTimeString('en-GB', options);
};

export const timeConvert = (n, extraTimePayment) => {
    const hours = Math.floor(n / 60);
    const minutes = n % 60;
    return (
        <div className="card">
            <div className="card-body">
                <div className="row text-center">
                    <div className="col-6 border-end border-light">
                        <h6 className="text-muted mt-1 mb-2 fw-normal">Hour(s)</h6>
                        <h6 className="mb-0 fw-bold">{hours}</h6>
                    </div>
                    <div className="col-6">
                        <h6 className="text-muted mt-1 mb-2 fw-normal">Minute(s)</h6>
                        <h6 className="mb-0 fw-bold">{minutes}</h6>
                    </div>
                </div>
                <hr />
                <p style={{ marginBottom: '0px' }}>
                    Extra Hours Total (GBP): {formatNumber(extraTimePayment)}
                </p>
            </div>
        </div>
    );
};

export const materialSubTotal = (item) => {
    const { request_material_unit_price: unitPrice, request_material_commission: commission } = item;
    return formatNumber(unitPrice * (commission || 1));
};

export const materialTotal = (item) => {
    const { request_material_qty: qty, request_material_unit_price: unitPrice, request_material_commission: commission } = item;
    return formatNumber(qty * unitPrice * (commission || 1));
};

export const fullDateFormat = (timestamp) => {
    const date = parseTimestamp(timestamp);
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
};

export const moneyFormat = formatNumber;

export const totalCost = (jobDetails) => {
    const { job_ftm_hourly_rate: rate, full_total_material_price: materialPrice, full_extra_time_payment: extraTime } = jobDetails;
    return moneyFormat(rate + materialPrice + extraTime);
};

export const truncateText = (text, maxLength) => (
    <Tooltip title={text}>
        <span>{text?.length > maxLength ? `${text.slice(0, maxLength)}...` : text || "N/A"}</span>
    </Tooltip>
);

export const getFirebaseJob = async (jobId) => {
    const querySnapshot = await jobsRef
        .where('job_id', '==', parseInt(jobId))
        .where('job_customer.id', '==', parseInt(user_info.customer_id))
        .get();

    return querySnapshot.docs.reduce((job, doc) => (doc.exists ? { doc_id: doc.id, ...doc.data() } : job), {});
};

export const getFirebaseDisputes = async (jobId) => {
    const querySnapshot = await disputesRef
        .where('dispute_job.job_id', '==', parseInt(jobId))
        .where('dispute_details.customer.id', '==', parseInt(user_info.customer_id))
        .get();

    return querySnapshot.docs.reduce((dispute, doc) => (doc.exists ? { doc_id: doc.id, ...doc.data() } : dispute), {});
};

export const getFirebaseDisputesByDocId = async (docId) => {
    const docSnapshot = await disputesRef.doc(docId).get();

    if (!docSnapshot.exists) {
        return {};
    }

    return {
        doc_id: docSnapshot.id,
        ...docSnapshot.data(),
    };
};

export const getPaymentStatus = (paymentStatus) => {
    const statuses = {
        1: { status: "PRE AUTH", icon: 'priority_high', text_color: 'dark' },
        2: { status: "CHARGE", icon: 'expand_less', text_color: 'success' },
        3: { status: "RELEASE", icon: 'expand_more', text_color: 'danger' },
        4: { status: "REFUND", icon: 'expand_more', text_color: 'danger' },
    };
    return statuses[paymentStatus] || { status: "N/A", icon: 'expand_more', text_color: 'danger' };
};

export const localeDateStringFormat = (timestamp) => {
    const options = {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
    };
    return parseTimestamp(timestamp).toLocaleDateString("en-US", options);
};

export const getCardTypeImage = (cardType) => {
    const cardMap = {
        VISA: "visa.png",
        DELTA: "visa.png",
        UKE: "visa.png",
        MC: "mastercard.png",
        MCDEBIT: "mastercard.png",
        MAESTRO: "maestro.png",
    };
    return cardMap[cardType] || "maestro.png";
};

export const findMatchingRoute = (givenPath) => {
    const matchingRoute = routes.find(route => new RegExp(`^${route.path.replace(/:\w+/g, '\\d+')}$$`).test(givenPath));
    return matchingRoute?.page_name || "No matching route found";
};

export const textReplace = ({ text, search, replace }) => text.replace(search, replace);

export const convertToMillions = (value) => {
    const amount = Number(value);
    const formatted = amount >= 1e9
        ? `${formatNumber(amount / 1e9)} B`
        : amount >= 1e6
            ? `${formatNumber(amount / 1e6)} M`
            : formatNumber(amount);
    return (
        <Tooltip title={amount} arrow placement="right">
            <span>{formatMoney(formatted)}</span>
        </Tooltip>
    );
};

export const timestampToDate = ({ timestamp, job_is_remedial_job }) => {
    const date = parseTimestamp(timestamp);
    const baseDate = `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
    return job_is_remedial_job ? baseDate : `${baseDate} ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
};

export const formatDateInJobStatsLog = (timestamp) => {
    const date = parseTimestamp(timestamp);
    const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
    const hours = date.getHours();
    const minutes = pad2(date.getMinutes());
    const amPm = hours >= 12 ? 'PM' : 'AM';
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()} ${hours}:${minutes} ${amPm}`;
};

export const handleDownload = (pdfLink) => window.open(pdfLink, '_blank', 'noopener,noreferrer');

export const errorAlertIconHtml = '<img src="/assets/img/privacy_tip_60dp_0EDB6F_FILL1_wght400_GRAD0_opsz48.png" width="120" height="120">';

export const isShowChatNotifications = async (jobId, messageType) => {
    PUT("admin/show/notification", { job_id: jobId, message_type: messageType });
};

export const loadScript = (url, onLoad, onError = () => console.error("Failed to load script.")) => {
    if (!document.getElementById("google-maps-script")) {
        const script = document.createElement("script");
        script.src = url;
        script.id = "google-maps-script";
        script.async = true;
        script.onload = onLoad;
        script.onerror = onError;
        document.head.appendChild(script);
    } else if (onLoad) onLoad();
};

export const createMarkerContent = (iconURL) => {
    const div = document.createElement("div");
    div.style.backgroundImage = `url(${iconURL})`;
    div.style.backgroundSize = "cover";
    div.style.width = "55px";
    div.style.height = "55px";
    return div;
};

export const formatTimestampToDate = (timestamp) => {
    const date = parseTimestamp(timestamp);
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

export const capitalizeWords = (input) => input?.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

export const formatMoney = (amount, currency = 'GBP', locale = 'en-GB') => {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount || 0);
};

export const getTimeDifference = (time1, time2) => {
    const date1 = new Date(time1).getTime();
    const date2 = new Date(time2).getTime();

    let diffInSeconds = Math.abs((date2 - date1) / 1000);

    const hours = Math.floor(diffInSeconds / 3600);
    diffInSeconds %= 3600;

    const minutes = Math.floor(diffInSeconds / 60);
    const seconds = Math.floor(diffInSeconds % 60);

    const pad = (num) => String(num).padStart(2, "0");

    return `${pad(hours)} : ${pad(minutes)} : ${pad(seconds)}`;
};

export const ftmRequestStatus = (status) => {
    switch (status) {
        case 1:
            return { icon: "check_circle", color: "success", text: "Accept" };
        case -1:
            return { icon: "cancel", color: "danger", text: "Reject" };
        case 2:
            return { icon: "help", color: "secondary", text: "Undecided" };
        default:
            return { icon: "error", color: "warning", text: "Pending" };
    }
};

export const formatPhoneNumber = (rawNumber, formatPattern) => {
    // Check if rawNumber is defined before attempting to use replace
    if (rawNumber === undefined) {
        return ""; // or handle the undefined case appropriately
    }

    // Keep only digits from the raw number
    const digits = rawNumber.replace(/\D/g, "");
    let digitIndex = 0;

    // Replace only digit placeholders in the format pattern (ignore its digits)
    return formatPattern.replace(/\d/g, () => digits[digitIndex++] || "");
};

export function formatTimeTo12Hour(timeString) {
    if (!timeString) return "";

    // Handle time string (HH:mm)
    let [hour, minute] = timeString?.split(":").map(Number);

    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12; // convert 0 -> 12, 13 -> 1

    return `${hour}:${minute?.toString().padStart(2, "0")} ${ampm}`;
};

export const isValidUKPostcode = (postcode) => {
    let postcodeRegex = /^[A-Za-z]{1,2}[0-9Rr][0-9A-Za-z]? [0-9][A-Za-z]{2}$/;
    return postcodeRegex.test(postcode);
};

export const encryptFormData = async (formData, publicKeyPem) => {
    // const publicKeyPem = await fetch("/keys/public.pem").then(res => res.text());

    const publicKey = forge.pki.publicKeyFromPem(publicKeyPem);

    const json = JSON.stringify(formData);

    const encryptedBytes = publicKey.encrypt(json, "RSA-OAEP", {
        md: forge.md.sha256.create(),
        mgf1: forge.mgf1.create(forge.md.sha1.create()), // Node default OAEP MGF1
    });

    const base64 = forge.util.encode64(encryptedBytes);
    return base64;
}

export const formatReadable = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
}

export const roundToOneDecimal = (num) => {
    return Math.round(num * 10) / 10;
};

export const convertUKToTimestamp = (dateString) => {
    // treat input as UK time
    const [datePart, timePart] = dateString.split(" ");
    const [year, month, day] = datePart.split("-");
    const [hour, minute, second] = timePart.split(":");

    // create UTC date using UK time
    const utcDate = new Date(
        Date.UTC(year, month - 1, day, hour, minute, second)
    );

    return utcDate.getTime();
};

// ✅ CUSTOMER animated marker with original icon
export const createAnimatedMarker = (icon) => {
    const container = document.createElement("div");

    container.innerHTML = `
            <div class="customer-marker-container">
                <div class="customer-pulse"></div>

                <img 
                    src="${icon}" 
                    class="customer-marker-icon"
                />
            </div>
        `;

    return container;
};

export const formatShortDateTime = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');

    const month = date.toLocaleString('en-US', {
        month: 'short',
    });

    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day} ${month}, ${hours}:${minutes}`;
};

export const formatJobTimeRange = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return "N/A";

    const checkInDate = new Date(Number(checkIn));
    const checkOutDate = new Date(Number(checkOut));

    const today = new Date();
    const isToday = checkInDate.toDateString() === today.toDateString();

    const formatTime = (date) =>
        date.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });

    const dayLabel = isToday
        ? "Today"
        : checkInDate.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
          });

    return `${dayLabel}, ${formatTime(checkInDate)} - ${formatTime(checkOutDate)}`;
};

export const formatBillableWorkTime = (mins) => {
    return mins >= 60 ? `${Math.floor(mins / 60)} hours` : `${mins} minutes`;
};

export const crystalizeDateTime = (dateTime) => {
    const date = new Date(dateTime);

    return {
        formattedDate: date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }),
        formattedTime: date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        }),
    };
};

export const formatMinutes = (minutes) => {
    if (!minutes || minutes < 0) return "0min";

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours && mins) return `${hours}h ${mins}min`;
    if (hours) return `${hours}h`;
    return `${mins}min`;
};